import { isOffsetDateTime, isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export const PROPERTY_PAGE_LIMIT = 25;
export interface PropertyCursor {
  before_created_at: string;
  before_property_id: string;
}
export type PropertySortDirection = 'asc' | 'desc';
export interface PropertySort {
  sortBy: 'name';
  sortDirection: PropertySortDirection;
  cursor?: string | null;
}
export type RoomSortBy = 'room_no' | 'floor';
export interface RoomSort {
  sortBy: RoomSortBy;
  sortDirection: PropertySortDirection;
  cursor?: string | null;
}
export interface PropertySummary {
  property_id: string;
  environment: 'dev' | 'prod';
  branch_id: number;
  code: string;
  name: string;
  application_enabled: boolean;
  version: number;
  /** 서버 원문을 보존한다. Date 변환 후 cursor로 직렬화하면 microsecond가 손실된다. */
  created_at: string;
}
export interface PropertyPage {
  items: PropertySummary[];
  nextLookup: PropertyCursor | null;
  nextCursor?: string | null;
}
export interface CatalogRoom {
  id: string;
  sourceRoomId: number;
  roomNo: number;
  typeId: number;
  typeName: string | null;
  /** Actual current Core parent; legacy sourceRoomTypeId remains an independent observation. */
  coreRoomTypeId: string | null;
  version: number | null;
  topologyRevision: number | null;
  floor: number | null;
  slotCapacity: number | null;
  sourceDeletedMarker: string | null;
  selectedForManagement: boolean | null;
  selectionVersion: number | null;
}
export interface CatalogRoomType {
  id: string;
  sourceRoomTypeId: number;
  name: string;
  sourceDeletedMarker: string | null;
  version: number | null;
}
export interface CatalogOwnership {
  id: string;
  state: string;
  effectiveFrom: string | null;
  effectiveUntil: string | null;
  version: number;
}
export interface CatalogSlot {
  id: string;
  roomId: string;
  slotNo: string;
  physicalCapacity: number | null;
  sourceDeletedMarker: string | null;
  ownerships: CatalogOwnership[];
}
export interface CatalogPresentation {
  id: string;
  targetKind: string;
  propertyId: string | null;
  roomTypeId: string | null;
  roomId: string | null;
  slotId: string | null;
  features: Record<string, unknown>;
  texts: CatalogPresentationText[];
}
export interface CatalogPresentationText {
  purpose: string;
  language: string;
  itemKey: string;
  value: string | null;
  sourceOrder: number | null;
  componentKey: string | null;
}
export interface CatalogMediaGroup {
  id: string;
  ownerScopeKind: 'PROPERTY' | 'ROOM_TYPE' | 'UNIT';
  ownerPropertyId: string;
  ownerRoomTypeId: string | null;
  ownerRoomId: string | null;
  ownerUnitId: string | null;
  ownerFloor: number | null;
  purpose: string;
  language: string;
  categoryName: string | null;
  sourceOrder: number | null;
  componentKey: string | null;
  sourceObservationState: string | null;
  sourceNamespace: string | null;
  sourceTable: string | null;
  sourceRecordKey: string | null;
  sourceField: string | null;
}
export interface CatalogMediaItem {
  id: string;
  groupId: string;
  kind: string;
  primaryUri: string | null;
  thumbnailUri: string | null;
  sourceOrder: number | null;
  contentSlot: string | null;
  sourceObservationState: string | null;
  sourceNamespace: string | null;
  sourceTable: string | null;
  sourceRecordKey: string | null;
  sourceField: string | null;
}
export interface CatalogPriorLegendProfile {
  id: string;
  scopeEnvironment: 'dev' | 'prod';
  scopePropertyId: string | null;
  scopeRoomTypeId: string | null;
  sourceObservationState: 'ABSENT' | 'QUARANTINED';
}
export interface PropertyCatalogCursors {
  room?: string | null;
  roomSort?: string | null;
  sortBy?: RoomSortBy;
  sortDirection?: PropertySortDirection;
}
export interface PropertyCatalog {
  initialBindingContract: 'INITIAL_TYPE_BINDING_V1' | 'LEGACY_READ_ONLY';
  property: PropertySummary & {
    sourceDeletedMarker: string | null;
  };
  roomTypes: CatalogRoomType[];
  rooms: CatalogRoom[];
  slots: CatalogSlot[];
  presentations: CatalogPresentation[];
  mediaGroups: CatalogMediaGroup[];
  mediaItems: CatalogMediaItem[];
  priorLegendProfiles: CatalogPriorLegendProfile[];
  roomNextCursor: string | null;
  roomSortNextCursor: string | null;
  totalRoomCount: number;
}
export interface ApplicationSettingDraft {
  enabled: boolean;
  expectedVersion: number;
  reason: string;
}
export interface ApplicationSettingResult {
  property_id: string;
  enabled: boolean;
  version: number;
  updated_at: string;
}
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);
const int = (value: unknown): value is number => Number.isSafeInteger(value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const nullableInt = (value: unknown): value is number | null => value === null || int(value);
const nullableSafeInt = (value: unknown): value is number | null => value === null || int(value);
const positiveRevision = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0 && Number(value) < 2 ** 63;
const nullableBoolean = (value: unknown): value is boolean | null =>
  value === null || typeof value === 'boolean';
const catalogText = (value: unknown, allowed: readonly string[]): value is string =>
  typeof value === 'string' && allowed.includes(value);
const environment = (value: unknown): value is 'dev' | 'prod' =>
  value === 'dev' || value === 'prod';
function invalid() {
  return new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '지점 정보를 불러오지 못했습니다. 관리자에게 문의해 주세요.',
  });
}
function summary(value: unknown): PropertySummary {
  if (
    !record(value) ||
    !isUuid(value.property_id) ||
    !environment(value.environment) ||
    !int(value.branch_id) ||
    !text(value.code) ||
    !text(value.name) ||
    typeof value.application_enabled !== 'boolean' ||
    !int(value.version) ||
    value.version < 1 ||
    !isOffsetDateTime(value.created_at)
  )
    throw invalid();
  return {
    property_id: value.property_id,
    environment: value.environment,
    branch_id: value.branch_id,
    code: value.code,
    name: value.name,
    application_enabled: value.application_enabled,
    version: value.version,
    created_at: value.created_at,
  };
}
export async function readProperties(
  client: ServiceClient,
  cursor?: PropertyCursor | null,
  signal?: AbortSignal,
  sort?: PropertySort | null,
): Promise<PropertyPage> {
  const sortBy: string = sort?.sortBy ?? '';
  if (
    cursor != null &&
    (!isUuid(cursor.before_property_id) || !isOffsetDateTime(cursor.before_created_at))
  )
    throw invalid();
  if (
    sort &&
    (sortBy !== 'name' ||
      !['asc', 'desc'].includes(sort.sortDirection) ||
      (sort.cursor != null && (typeof sort.cursor !== 'string' || sort.cursor.length === 0)) ||
      cursor != null)
  )
    throw invalid();
  const request = await client.get<unknown>('admin/properties', {
    params: {
      limit: PROPERTY_PAGE_LIMIT,
      ...(sort
        ? {
            sort_by: sortBy,
            sort_direction: sort.sortDirection,
            cursor: sort.cursor ?? undefined,
          }
        : (cursor ?? {})),
    },
    preserveEnvelope: Boolean(sort),
    signal,
  });
  const value = sort
    ? record(request) &&
      Array.isArray(request.data) &&
      (request.next_cursor === null ||
        (typeof request.next_cursor === 'string' && request.next_cursor.length > 0))
      ? request.data
      : null
    : request;
  const nextCursor = sort && record(request) ? (request.next_cursor as string | null) : undefined;
  if (!Array.isArray(value) || value.length > PROPERTY_PAGE_LIMIT) throw invalid();
  const items = value.map(summary);
  const last = items.at(-1);
  // 서버는 has_more를 주지 않는다. 다음 조회의 존재 가능성만 제공하며 빈 응답일 수 있다.
  return {
    items,
    nextLookup: last
      ? sort
        ? null
        : { before_created_at: last.created_at, before_property_id: last.property_id }
      : null,
    ...(sort ? { nextCursor } : {}),
  };
}
export async function readPropertyCatalog(
  client: ServiceClient,
  propertyId: string,
  cursors: PropertyCatalogCursors = {},
  signal?: AbortSignal,
): Promise<PropertyCatalog> {
  if (
    !isUuid(propertyId) ||
    [cursors.room].some((cursor) => cursor != null && !isUuid(cursor)) ||
    (cursors.room != null && cursors.roomSort != null) ||
    cursors.roomSort?.length === 0 ||
    (cursors.sortBy === undefined) !== (cursors.sortDirection === undefined) ||
    (cursors.sortBy !== undefined && !['room_no', 'floor'].includes(cursors.sortBy)) ||
    (cursors.sortDirection !== undefined && !['asc', 'desc'].includes(cursors.sortDirection)) ||
    (cursors.room != null && cursors.sortBy !== undefined) ||
    (cursors.roomSort != null && cursors.sortBy === undefined) ||
    (cursors.sortBy === undefined && cursors.roomSort != null)
  )
    throw invalid();
  const value = await client.get<unknown>(`admin/properties/${propertyId}/catalog`, {
    params: {
      limit: PROPERTY_PAGE_LIMIT,
      room_cursor: cursors.room ?? undefined,
      sort_by: cursors.sortBy,
      sort_direction: cursors.sortDirection,
      room_sort_cursor: cursors.roomSort ?? undefined,
    },
    signal,
  });
  if (
    !record(value) ||
    !record(value.property) ||
    !Array.isArray(value.room_types) ||
    !Array.isArray(value.rooms) ||
    !Array.isArray(value.slots) ||
    !Array.isArray(value.presentations) ||
    !Array.isArray(value.media_groups) ||
    !Array.isArray(value.media_items) ||
    !Array.isArray(value.legend_profiles) ||
    !Array.isArray(value.prior_legend_profiles) ||
    value.rooms.length > PROPERTY_PAGE_LIMIT ||
    value.slots.length > 500 ||
    value.presentations.length > 500 ||
    value.media_groups.length > 1_000 ||
    value.media_items.length > 2_000 ||
    value.legend_profiles.length + value.prior_legend_profiles.length > 500 ||
    !(value.room_next_cursor === null || isUuid(value.room_next_cursor)) ||
    !int(value.total_room_count) ||
    value.total_room_count < value.rooms.length
  )
    throw invalid();
  const sortedRooms = cursors.sortBy !== undefined;
  if (
    sortedRooms
      ? !(
          value.room_sort_next_cursor === null ||
          (typeof value.room_sort_next_cursor === 'string' &&
            value.room_sort_next_cursor.length > 0)
        ) || value.room_next_cursor !== null
      : 'room_sort_next_cursor' in value
  )
    throw invalid();
  const p = value.property;
  const bindingExtensionPresent =
    value.room_types.some((type) => record(type) && 'version' in type) ||
    value.rooms.some(
      (room) =>
        record(room) &&
        ('version' in room || 'room_type_id' in room || 'topology_revision' in room),
    );
  const bindingContract = bindingExtensionPresent
    ? ('INITIAL_TYPE_BINDING_V1' as const)
    : ('LEGACY_READ_ONLY' as const);
  if (
    !isUuid(p.id) ||
    p.id.toLowerCase() !== propertyId.toLowerCase() ||
    !nullableText(p.source_deleted_marker)
  )
    throw invalid();
  const property = {
    ...summary({
      property_id: p.id,
      environment: p.source_environment,
      branch_id: p.source_branch_id,
      code: p.source_code,
      name: p.source_name,
      application_enabled: p.application_enabled,
      version: p.version,
      created_at: p.created_at,
    }),
    sourceDeletedMarker: p.source_deleted_marker,
  };
  const types = new Map<number, CatalogRoomType>();
  for (const type of value.room_types) {
    if (
      !record(type) ||
      !isUuid(type.id) ||
      !int(type.source_room_type_id) ||
      !text(type.source_name) ||
      !nullableText(type.source_deleted_marker) ||
      (bindingExtensionPresent && (!('version' in type) || !positiveRevision(type.version))) ||
      types.has(type.source_room_type_id)
    )
      throw invalid();
    types.set(type.source_room_type_id, {
      id: type.id,
      sourceRoomTypeId: type.source_room_type_id,
      name: type.source_name,
      sourceDeletedMarker: type.source_deleted_marker,
      version: positiveRevision(type.version) ? type.version : null,
    });
  }
  const rooms = value.rooms.map((room): CatalogRoom => {
    if (
      !record(room) ||
      !isUuid(room.id) ||
      room.source_environment !== property.environment ||
      !int(room.source_room_id) ||
      !int(room.source_room_type_id) ||
      !int(room.source_room_no) ||
      (bindingExtensionPresent &&
        (!('room_type_id' in room) ||
          !(room.room_type_id === null || isUuid(room.room_type_id)))) ||
      (bindingExtensionPresent &&
        (!('version' in room) ||
          !positiveRevision(room.version) ||
          !('topology_revision' in room) ||
          !positiveRevision(room.topology_revision))) ||
      !nullableInt(room.source_floor) ||
      !nullableInt(room.source_slot_capacity) ||
      !nullableText(room.source_deleted_marker) ||
      !nullableBoolean(room.selected_for_management) ||
      !nullableInt(room.selection_version) ||
      (room.selected_for_management === null) !== (room.selection_version === null)
    )
      throw invalid();
    return {
      id: room.id,
      sourceRoomId: room.source_room_id,
      roomNo: room.source_room_no,
      typeId: room.source_room_type_id,
      typeName: types.get(room.source_room_type_id)?.name ?? null,
      coreRoomTypeId: isUuid(room.room_type_id) ? room.room_type_id : null,
      version: positiveRevision(room.version) ? room.version : null,
      topologyRevision: positiveRevision(room.topology_revision) ? room.topology_revision : null,
      floor: room.source_floor,
      slotCapacity: room.source_slot_capacity,
      sourceDeletedMarker: room.source_deleted_marker,
      selectedForManagement: room.selected_for_management,
      selectionVersion: room.selection_version,
    };
  });
  const slots = value.slots.map((slot): CatalogSlot => {
    if (
      !record(slot) ||
      !isUuid(slot.id) ||
      !isUuid(slot.room_id) ||
      !rooms.some((room) => room.id === slot.room_id) ||
      !text(slot.source_slot_no) ||
      !nullableInt(slot.physical_capacity) ||
      !nullableText(slot.source_deleted_marker) ||
      !Array.isArray(slot.ownerships)
    )
      throw invalid();
    const ownerships = slot.ownerships.map((ownership): CatalogOwnership => {
      if (
        !record(ownership) ||
        !isUuid(ownership.id) ||
        !catalogText(ownership.state, [
          'DISCOVERED',
          'ELIGIBILITY_CHECKED',
          'FENCE_PREPARED',
          'BARRIER_CHECKED',
          'ACTIVE',
          'SUSPENDED',
          'DRAINING',
          'RETURN_READY',
          'RETURNED',
        ]) ||
        !isOffsetDateTime(ownership.effective_from) ||
        !(ownership.effective_until === null || isOffsetDateTime(ownership.effective_until)) ||
        !int(ownership.version)
      )
        throw invalid();
      return {
        id: ownership.id,
        state: ownership.state,
        effectiveFrom: ownership.effective_from,
        effectiveUntil: ownership.effective_until,
        version: ownership.version,
      };
    });
    return {
      id: slot.id,
      roomId: slot.room_id,
      slotNo: slot.source_slot_no,
      physicalCapacity: slot.physical_capacity,
      sourceDeletedMarker: slot.source_deleted_marker,
      ownerships,
    };
  });
  if (slots.reduce((count, slot) => count + slot.ownerships.length, 0) > 500) throw invalid();
  const presentations = value.presentations.map((presentation): CatalogPresentation => {
    if (
      !record(presentation) ||
      !isUuid(presentation.id) ||
      !catalogText(presentation.target_kind, [
        'PROPERTY',
        'ROOM_TYPE',
        'ROOM',
        'SLOT',
        'PROPERTY_FLOOR',
      ]) ||
      (presentation.property_id !== undefined &&
        !(presentation.property_id === null || isUuid(presentation.property_id))) ||
      (presentation.room_type_id !== undefined &&
        !(presentation.room_type_id === null || isUuid(presentation.room_type_id))) ||
      (presentation.room_id !== undefined &&
        !(presentation.room_id === null || isUuid(presentation.room_id))) ||
      (presentation.slot_id !== undefined &&
        !(presentation.slot_id === null || isUuid(presentation.slot_id))) ||
      (presentation.features !== undefined && !record(presentation.features)) ||
      (presentation.texts !== undefined && !Array.isArray(presentation.texts))
    )
      throw invalid();
    return {
      id: presentation.id,
      targetKind: presentation.target_kind,
      propertyId: text(presentation.property_id) ? presentation.property_id : null,
      roomTypeId: text(presentation.room_type_id) ? presentation.room_type_id : null,
      roomId: text(presentation.room_id) ? presentation.room_id : null,
      slotId: text(presentation.slot_id) ? presentation.slot_id : null,
      features: record(presentation.features) ? presentation.features : {},
      texts: (Array.isArray(presentation.texts) ? presentation.texts : []).map((item) => {
        if (
          !record(item) ||
          !text(item.purpose) ||
          !text(item.language) ||
          !text(item.item_key) ||
          !(item.value === null || text(item.value)) ||
          !(item.source_order === undefined || nullableSafeInt(item.source_order)) ||
          !(item.component_key === undefined || nullableText(item.component_key))
        )
          throw invalid();
        return {
          purpose: item.purpose,
          language: item.language,
          itemKey: item.item_key,
          value: item.value,
          sourceOrder: nullableSafeInt(item.source_order) ? item.source_order : null,
          componentKey: nullableText(item.component_key) ? item.component_key : null,
        };
      }),
    };
  });
  const mediaGroups = value.media_groups.map((group): CatalogMediaGroup => {
    if (
      !record(group) ||
      !isUuid(group.id) ||
      !catalogText(group.owner_scope_kind, ['PROPERTY', 'ROOM_TYPE', 'UNIT']) ||
      !isUuid(group.owner_property_id) ||
      !(group.owner_room_type_id === null || isUuid(group.owner_room_type_id)) ||
      !(group.owner_room_id === null || isUuid(group.owner_room_id)) ||
      !(group.owner_unit_id === null || isUuid(group.owner_unit_id)) ||
      !(group.owner_floor === null || int(group.owner_floor)) ||
      !text(group.purpose) ||
      !text(group.language) ||
      !(group.category_name === undefined || nullableText(group.category_name)) ||
      !(group.source_order === undefined || nullableSafeInt(group.source_order)) ||
      !(group.component_key === undefined || nullableText(group.component_key)) ||
      !(group.source_observation_state === null || text(group.source_observation_state)) ||
      (group.provenance !== undefined && !record(group.provenance))
    )
      throw invalid();
    const ownerScopeValid =
      (group.owner_scope_kind === 'PROPERTY' &&
        group.owner_room_type_id === null &&
        group.owner_room_id === null &&
        group.owner_unit_id === null) ||
      (group.owner_scope_kind === 'ROOM_TYPE' &&
        isUuid(group.owner_room_type_id) &&
        group.owner_room_id === null &&
        group.owner_unit_id === null) ||
      (group.owner_scope_kind === 'UNIT' &&
        isUuid(group.owner_room_id) &&
        isUuid(group.owner_unit_id));
    if (!ownerScopeValid) throw invalid();
    const ownerScopeKind = group.owner_scope_kind as CatalogMediaGroup['ownerScopeKind'];
    const provenance = record(group.provenance) ? group.provenance : {};
    const records = Array.isArray(provenance.records) ? provenance.records : [];
    const source = records.length === 1 && record(records[0]) ? records[0] : {};
    if (
      (provenance.records !== undefined && !Array.isArray(provenance.records)) ||
      (source.source_table !== undefined && !text(source.source_table)) ||
      (source.record_key !== undefined && !text(source.record_key)) ||
      (source.namespace !== undefined && !text(source.namespace)) ||
      (source.field !== undefined && !text(source.field))
    )
      throw invalid();
    return {
      id: group.id,
      ownerScopeKind,
      ownerPropertyId: group.owner_property_id,
      ownerRoomTypeId: group.owner_room_type_id,
      ownerRoomId: group.owner_room_id,
      ownerUnitId: group.owner_unit_id,
      ownerFloor: group.owner_floor,
      purpose: group.purpose,
      language: group.language,
      categoryName: nullableText(group.category_name) ? group.category_name : null,
      sourceOrder: nullableSafeInt(group.source_order) ? group.source_order : null,
      componentKey: nullableText(group.component_key) ? group.component_key : null,
      sourceObservationState: text(group.source_observation_state)
        ? group.source_observation_state
        : null,
      sourceNamespace: text(source.namespace) ? source.namespace : null,
      sourceTable: text(source.source_table) ? source.source_table : null,
      sourceRecordKey: text(source.record_key) ? source.record_key : null,
      sourceField: text(source.field) ? source.field : null,
    };
  });
  const mediaItems = value.media_items.map((item): CatalogMediaItem => {
    if (
      !record(item) ||
      !isUuid(item.id) ||
      !isUuid(item.group_id) ||
      !text(item.kind) ||
      !(item.primary_uri === null || text(item.primary_uri)) ||
      !(item.thumbnail_uri === null || text(item.thumbnail_uri)) ||
      !(item.source_order === undefined || nullableSafeInt(item.source_order)) ||
      !(item.content_slot === undefined || nullableText(item.content_slot)) ||
      !(item.source_observation_state === null || text(item.source_observation_state)) ||
      (item.provenance !== undefined && !record(item.provenance))
    )
      throw invalid();
    const provenance = record(item.provenance) ? item.provenance : {};
    const records = Array.isArray(provenance.records) ? provenance.records : [];
    const source = records.length === 1 && record(records[0]) ? records[0] : {};
    if (
      (provenance.records !== undefined && !Array.isArray(provenance.records)) ||
      (source.source_table !== undefined && !text(source.source_table)) ||
      (source.record_key !== undefined && !text(source.record_key)) ||
      (source.namespace !== undefined && !text(source.namespace)) ||
      (source.field !== undefined && !text(source.field))
    )
      throw invalid();
    return {
      id: item.id,
      groupId: item.group_id,
      kind: item.kind,
      primaryUri: item.primary_uri,
      thumbnailUri: item.thumbnail_uri,
      sourceOrder: nullableSafeInt(item.source_order) ? item.source_order : null,
      contentSlot: nullableText(item.content_slot) ? item.content_slot : null,
      sourceObservationState: text(item.source_observation_state)
        ? item.source_observation_state
        : null,
      sourceNamespace: text(source.namespace) ? source.namespace : null,
      sourceTable: text(source.source_table) ? source.source_table : null,
      sourceRecordKey: text(source.record_key) ? source.record_key : null,
      sourceField: text(source.field) ? source.field : null,
    };
  });
  const priorLegendProfiles = value.prior_legend_profiles.map(
    (profile): CatalogPriorLegendProfile => {
      if (
        !record(profile) ||
        !isUuid(profile.id) ||
        !environment(profile.scope_environment) ||
        !(profile.scope_property_id === null || isUuid(profile.scope_property_id)) ||
        !(profile.scope_room_type_id === null || isUuid(profile.scope_room_type_id)) ||
        !catalogText(profile.source_observation_state, ['ABSENT', 'QUARANTINED'])
      )
        throw invalid();
      return {
        id: profile.id,
        scopeEnvironment: profile.scope_environment,
        scopePropertyId: profile.scope_property_id,
        scopeRoomTypeId: profile.scope_room_type_id,
        sourceObservationState:
          profile.source_observation_state as CatalogPriorLegendProfile['sourceObservationState'],
      };
    },
  );
  return {
    initialBindingContract: bindingContract,
    property,
    roomTypes: [...types.values()],
    rooms,
    slots,
    presentations,
    mediaGroups,
    mediaItems,
    priorLegendProfiles,
    roomNextCursor: value.room_next_cursor,
    roomSortNextCursor: sortedRooms ? (value.room_sort_next_cursor as string | null) : null,
    totalRoomCount: value.total_room_count,
  };
}

export async function changePropertyApplicationSetting(
  client: ServiceClient,
  propertyId: string,
  draft: ApplicationSettingDraft,
): Promise<ApplicationSettingResult> {
  if (
    !isUuid(propertyId) ||
    typeof draft.enabled !== 'boolean' ||
    !int(draft.expectedVersion) ||
    draft.expectedVersion < 1 ||
    !text(draft.reason) ||
    !draft.reason.trim() ||
    draft.reason.length > 240
  )
    throw invalid();
  const value = await client.patch<unknown>(`admin/properties/${propertyId}/application-setting`, {
    json: {
      enabled: draft.enabled,
      expected_version: draft.expectedVersion,
      reason: draft.reason,
    },
  });
  if (
    !record(value) ||
    !isUuid(value.property_id) ||
    value.property_id.toLowerCase() !== propertyId.toLowerCase() ||
    typeof value.enabled !== 'boolean' ||
    !int(value.version) ||
    value.version < 1 ||
    !isOffsetDateTime(value.updated_at)
  )
    throw invalid();
  return {
    property_id: value.property_id,
    enabled: value.enabled,
    version: value.version,
    updated_at: value.updated_at,
  };
}
